/**
 * Address Autocomplete Utility
 *
 * Provides address suggestions using Amazon Location Service with API key auth.
 * Uses @aws/amazon-location-utilities-auth-helper for authentication.
 *
 * Features:
 * - 300ms debouncing to minimize API calls
 * - Minimum 3 characters before triggering
 * - Biased toward Tumbulgum, NSW region
 * - Filtered to Australian addresses only
 * - Graceful degradation when API key not configured
 */

import {
  LocationClient,
  SearchPlaceIndexForSuggestionsCommand,
  GetPlaceCommand,
} from "@aws-sdk/client-location";
import { withAPIKey } from "@aws/amazon-location-utilities-auth-helper";
import { logDebug } from './logger';

export interface AddressSuggestion {
  text: string;
  placeId?: string;
}

export interface ParsedAddress {
  streetAddress: string; // e.g., "7 Sturgeon St"
  suburb: string; // e.g., "Ormiston"
  state: string; // e.g., "Queensland"
  postCode: string; // e.g., "4160"
}

const DEBOUNCE_MS = 300;
const MIN_CHARACTERS = 3;
const REGION = "ap-southeast-2";
const PLACE_INDEX = "TumbulgumFishingClub-PlaceIndex";

// Tumbulgum, NSW coordinates for bias [lng, lat]
const BIAS_POSITION = [153.4833, -28.2667];

let debounceTimer: ReturnType<typeof setTimeout> | null = null;
let locationClient: LocationClient | null = null;

/**
 * Get or create the Location Service client using API key auth
 */
const getClient = (): LocationClient | null => {
  if (locationClient) return locationClient;

  const apiKey = import.meta.env.VITE_LOCATION_API_KEY;
  if (!apiKey) {
    console.warn(
      "REACT_APP_LOCATION_API_KEY not set — address autocomplete disabled.",
    );
    return null;
  }

  try {
    const authHelper = withAPIKey(apiKey, REGION);
    locationClient = new LocationClient({
      ...authHelper.getClientConfig(),
      region: REGION,
    });
    return locationClient;
  } catch (error) {
    console.error("Failed to create Location client:", error);
    return null;
  }
};

/**
 * Fetch address suggestions from Amazon Location Service
 */
export const fetchAddressSuggestions = async (
  query: string,
): Promise<AddressSuggestion[]> => {
  if (query.length < MIN_CHARACTERS) return [];

  const client = getClient();
  if (!client) return [];

  try {
    const command = new SearchPlaceIndexForSuggestionsCommand({
      IndexName: PLACE_INDEX,
      Text: query,
      BiasPosition: BIAS_POSITION,
      FilterCountries: ["AUS"],
      MaxResults: 5,
    });

    const response = await client.send(command);

    return (response.Results || []).map((result: any) => ({
      text: result.Text || "",
      placeId: result.PlaceId,
    }));
  } catch (error) {
    console.error("Address autocomplete error:", error);
    return [];
  }
};

/**
 * Debounced address autocomplete function
 */
export const debouncedAddressAutocomplete = (
  query: string,
  callback: (suggestions: AddressSuggestion[]) => void,
): void => {
  if (debounceTimer) clearTimeout(debounceTimer);

  debounceTimer = setTimeout(async () => {
    const suggestions = await fetchAddressSuggestions(query);
    callback(suggestions);
  }, DEBOUNCE_MS);
};

/**
 * Cancel any pending debounced autocomplete requests
 */
export const cancelPendingAutocomplete = (): void => {
  if (debounceTimer) {
    clearTimeout(debounceTimer);
    debounceTimer = null;
  }
};

/**
 * Get detailed place information from Amazon Location Service
 * Maps Amazon Location Service fields to our data model:
 *   Place.AddressNumber + Place.Street → streetAddress (our 'address' field)
 *   Place.Municipality → suburb (our 'suburb' field)
 *   Place.Region → state (our 'state' field)
 *   Place.PostalCode → postCode (our 'postCode' field)
 */
export const getPlaceDetails = async (
  placeId: string,
): Promise<ParsedAddress | null> => {
  const client = getClient();
  if (!client) return null;

  try {
    const command = new GetPlaceCommand({
      IndexName: PLACE_INDEX,
      PlaceId: placeId,
    });

    const response = await client.send(command);
    const place = response.Place;

    logDebug('addressAutocomplete', "GetPlace API response:", place);

    if (!place) return null;

    // Construct street address from AddressNumber and Street
    const addressNumber = place.AddressNumber || "";
    const street = place.Street || "";
    const streetAddress = [addressNumber, street].filter(Boolean).join(" ");

    logDebug('addressAutocomplete', "Parsed components:", {
      addressNumber,
      street,
      streetAddress,
      municipality: place.Municipality,
      region: place.Region,
      postalCode: place.PostalCode,
    });

    return {
      streetAddress,
      suburb: place.Municipality || "",
      state: place.Region || "",
      postCode: place.PostalCode || "",
    };
  } catch (error) {
    console.error("Failed to get place details:", error);
    return null;
  }
};

/**
 * Parse an Australian address string into structured components.
 * Used as a fallback when GetPlace API is unavailable (e.g., permission denied).
 *
 * Expected format from Amazon Location Service suggestions:
 *   "7 Sturgeon St, Ormiston, Brisbane, Queensland, 4160, AUS"
 *   "123 Main Road, Tumbulgum, New South Wales, 2490, AUS"
 *
 * Australian state names and abbreviations are matched to extract state + postcode.
 */
const AUSTRALIAN_STATES: Record<string, string> = {
  "new south wales": "New South Wales",
  nsw: "New South Wales",
  queensland: "Queensland",
  qld: "Queensland",
  victoria: "Victoria",
  vic: "Victoria",
  "south australia": "South Australia",
  sa: "South Australia",
  "western australia": "Western Australia",
  wa: "Western Australia",
  tasmania: "Tasmania",
  tas: "Tasmania",
  "northern territory": "Northern Territory",
  nt: "Northern Territory",
  "australian capital territory": "Australian Capital Territory",
  act: "Australian Capital Territory",
};

/**
 * Matches a subpremise / unit line segment, e.g. "Unit 10", "Apt 5",
 * "Suite 2", "Level 3", "Shop 4", "Flat 1", "Villa 7". Requires a trailing
 * number so it won't false-positive on real suburbs (e.g. "Ulmarra").
 * These segments belong in the street address, NOT the suburb field.
 */
const SUBPREMISE_PATTERN =
  /^(?:unit|apartment|apt|suite|ste|level|lvl|floor|fl|shop|villa|lot|flat|room|rm|penthouse|ph|u)\b\.?\s*\d/i;

/**
 * Returns true if a text segment looks like a unit/subpremise line rather than
 * a suburb (locality). Exported so form validation can flag misfiled suburbs.
 */
export const looksLikeSubpremise = (text: string): boolean =>
  SUBPREMISE_PATTERN.test((text || "").trim());

export const parseAustralianAddress = (text: string): ParsedAddress | null => {
  if (!text) return null;

  // Split by comma and trim each part
  const parts = text.split(",").map((p) => p.trim());
  if (parts.length < 3) return null;

  // Remove trailing "AUS" / "Australia" if present
  const lastPart = parts[parts.length - 1].toLowerCase();
  if (lastPart === "aus" || lastPart === "australia") {
    parts.pop();
  }

  // Try to find postcode (4-digit number) — usually second-to-last or last
  let postCode = "";
  let postCodeIndex = -1;
  for (let i = parts.length - 1; i >= 1; i--) {
    if (/^\d{4}$/.test(parts[i])) {
      postCode = parts[i];
      postCodeIndex = i;
      break;
    }
  }

  // Try to find state — usually just before postcode
  let state = "";
  let stateIndex = -1;
  const searchStart = postCodeIndex > 0 ? postCodeIndex - 1 : parts.length - 1;
  for (let i = searchStart; i >= 1; i--) {
    const normalized = parts[i].toLowerCase();
    if (AUSTRALIAN_STATES[normalized]) {
      state = AUSTRALIAN_STATES[normalized];
      stateIndex = i;
      break;
    }
  }

  // The locality section sits between the street (index 0) and the
  // state/postcode. Anything after it (e.g. a greater-city name) is ignored.
  const localityEnd =
    stateIndex > 0
      ? stateIndex
      : postCodeIndex > 0
        ? postCodeIndex
        : parts.length;
  const middle = parts.slice(1, localityEnd);

  // The suburb is the first middle segment that ISN'T a unit/subpremise line.
  // Unit lines that appear before the suburb are folded into the street address
  // so a value like "Unit 10" never ends up mislabelled as the suburb.
  let suburb = "";
  let suburbFound = false;
  const streetExtras: string[] = [];
  for (const part of middle) {
    if (suburbFound) break;
    if (looksLikeSubpremise(part)) {
      streetExtras.push(part);
    } else {
      suburb = part;
      suburbFound = true;
    }
  }

  const streetAddress = [parts[0], ...streetExtras].join(", ");

  return { streetAddress, suburb, state, postCode };
};
